import type { ChartViewMode, ChartBucketKind } from '@/utils/chartRangeNormalizer';
import type { Ref } from 'vue';
import { ref, watch, computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { formatCurrency } from '@/utils/formatUtils';
import { externalTooltip, bucketTitle } from '@/utils/chartTooltip';
import {
    rangeIntersectsCurrentChartPeriod,
    chartBucketRange,
    chartBucketKind,
} from '@/utils/chartRangeNormalizer';
import { getPaymentsChart, getDailyPaymentAverage } from '@/services/dashboardService';
import { useTimezoneStore } from '@/stores/timezone';
import PAYMENT_CHART_COLORS from '@/utils/paymentChartColors';

const DATASET_BASE = Object.freeze({
    backgroundColor: PAYMENT_CHART_COLORS.cleared,
    borderRadius: { topLeft: 12, topRight: 12 },
    borderSkipped: 'bottom' as const,
    maxBarThickness: 80,
});

const BUCKET_COLOR: Record<ChartBucketKind, string> = {
    cleared: PAYMENT_CHART_COLORS.cleared,
    upcoming: PAYMENT_CHART_COLORS.upcoming,
    mixed: PAYMENT_CHART_COLORS.cleared,
};

const BUCKET_LABEL: Record<ChartBucketKind, string> = {
    cleared: 'Cleared',
    upcoming: 'Upcoming / Pending',
    mixed: 'Cleared + Upcoming',
};

const BUCKET_TAB: Record<ChartBucketKind, string | undefined> = {
    cleared: 'deposits',
    upcoming: 'other',
    mixed: undefined,
};

type CachedEntry = {
    labels: string[];
    values: number[];
    from: string;
    to: string;
};

const EMPTY_ENTRY: CachedEntry = {
    labels: [], values: [], from: '', to: '',
};

export const useDashboardChartData = (dateRange: Ref<[string, string] | null>, options: { enabled?: boolean } = {}) => {
    const { enabled = true } = options;
    const toast = useToast();
    const tzStore = useTimezoneStore();
    const router = useRouter();

    const chartViewMode = ref<ChartViewMode>('days');
    const chartDataLoading = ref(true);
    const dailyPaymentAverageLoading = ref(true);
    const cache = reactive(new Map<ChartViewMode, CachedEntry>());
    const dailyPaymentAverage = ref(0);

    const currentEntry = computed<CachedEntry>(
        () => cache.get(chartViewMode.value) ?? EMPTY_ENTRY,
    );

    const bucketAt = (index: number) => {
        const { from, to } = currentEntry.value;
        if (!from || !to) return null;
        const bucket = chartBucketRange(from, to, chartViewMode.value, index);
        return { bucket, kind: chartBucketKind(bucket, tzStore.effectiveTimezone) };
    };

    const chartData = computed(() => ({
        labels: currentEntry.value.labels,
        datasets: [{
            ...DATASET_BASE,
            data: currentEntry.value.values,
            backgroundColor: currentEntry.value.values.map(
                (_, index) => BUCKET_COLOR[bucketAt(index)?.kind ?? 'cleared'],
            ),
        }],
    }));

    const dailyAverage = computed(() => formatCurrency(dailyPaymentAverage.value));

    const rangeIncludesCurrentPeriod = computed(() => {
        const range = dateRange.value;
        if (!range) return false;
        const [start, end] = range;
        return rangeIntersectsCurrentChartPeriod(start, end, chartViewMode.value, tzStore.effectiveTimezone);
    });

    const openBucket = (index: number) => {
        const hit = bucketAt(index);
        if (!hit) return;

        const tab = BUCKET_TAB[hit.kind];
        router.push({
            path: '/payments',
            query: {
                ...(tab ? { tab } : {}),
                from: hit.bucket.from,
                to: hit.bucket.to,
            },
        });
    };

    const chartOptions = computed(() => ({
        interaction: { mode: 'index' as const, intersect: true },

        plugins: {
            legend: { display: false },
            tooltip: externalTooltip({
                title: (items: Array<{ dataIndex: number; label: string }>) => {
                    const item = items[0];
                    if (!item) return '';
                    return bucketTitle(item.label, chartViewMode.value, bucketAt(item.dataIndex)?.bucket ?? null);
                },
                label: (ctx: { dataIndex: number; parsed: { y: number } }) => {
                    const hit = bucketAt(ctx.dataIndex);
                    const name = hit ? BUCKET_LABEL[hit.kind] : 'Payments';
                    return `${name}: ${formatCurrency(ctx.parsed.y ?? 0)}`;
                },
            }),
        },
    }));

    const fetchChartData = async () => {
        const range = dateRange.value;
        if (!range) return;
        const [startDate, endDate] = range;

        chartDataLoading.value = true;
        try {
            const response = await getPaymentsChart(startDate, endDate, chartViewMode.value, tzStore.effectiveTimezone);
            cache.set(chartViewMode.value, {
                labels: response.labels,
                values: response.values.map(Number),
                from: response.from,
                to: response.to,
            });
        } catch (error: any) {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: error?.message || 'Failed to fetch chart data',
                life: 3000,
            });
        } finally {
            chartDataLoading.value = false;
        }
    };

    const fetchDailyPaymentAverage = async () => {
        const range = dateRange.value;
        if (!range) return;
        const [startDate, endDate] = range;
        dailyPaymentAverageLoading.value = true;
        try {
            dailyPaymentAverage.value = await getDailyPaymentAverage(startDate, endDate);
        } catch (error: any) {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: error?.message || 'Failed to fetch daily payment average',
            });
        } finally {
            dailyPaymentAverageLoading.value = false;
        }
    };

    watch([dateRange, () => tzStore.effectiveTimezone], () => {
        if (!enabled) return;
        cache.clear();
        fetchChartData();
        fetchDailyPaymentAverage();
    }, { immediate: true });

    watch(chartViewMode, () => {
        if (!enabled) return;
        if (!cache.has(chartViewMode.value)) {
            fetchChartData();
        }
    });

    return {
        chartViewMode,
        chartData,
        chartOptions,
        openBucket,
        chartDataLoading,
        dailyPaymentAverageLoading,
        dailyAverage,
        rangeIncludesCurrentPeriod,
    };
};
